<template>
  <container-loader v-if="!loaded"></container-loader>
  <div v-else>
    <div class="table table--sharing">
      <b-table :fields="fields" :items="users" primary-key="id">
        <template v-slot:cell(user)="data">
          {{data.item.firstname}} {{data.item.lastname}}
        </template>
        <template v-slot:cell(limit)="data">
          {{limitText(data.item)}}
        </template>
        <template v-slot:cell(del)="data">
          <b-button size="-txt">
            <i v-if="data.item.printer_ownership_id!=1" class="icon icon--trash" @click="unshare(data.item.id)">
              <icon-trash />
            </i>
            <i v-else
               class="icon icon--crown"
               v-b-tooltip.hover
               :title="$t('printers.modal.sharePrinter.ownerCantBeDeleted')">
              <icon-crown />
            </i>
          </b-button>
        </template>
      </b-table>
    </div>
    <b-form class="form form--inline form--sharing">
      <b-form-textarea class="txtar" :placeholder="$t('printers.modal.sharePrinter.emailsToAllow')" :state="isValidEmail" v-model="emailValue"></b-form-textarea>
      <div>
        <b-select class="sel inp" v-model="roleValue">
          <option v-for="(roleName, key, index) in roles" :value="key" :key="index">{{roleName}}</option>
        </b-select>
        <b-input class="inp" :placeholder="$t('printers.modal.sharePrinter.limit')" v-model="limitValue"></b-input>
      </div>
      <b-button variant="-primary" @click="share">{{$t('printers.modal.sharePrinter.add')}}</b-button>
    </b-form>
    <b-modal v-model="showProgress" centered no-close-on-backdrop no-close-on-esc hide-header-close size="lg">
      <div class="table table--sharing">
      <b-table :fields="progressFields" :items="emails">
        <template v-slot:cell(type)="data">
          <span v-if="data.item.code === 1">
            {{$t('printers.modal.sharePrinter.notExists')}}
            <icon-info-circle v-b-tooltip.hover.leftbottom
                              :title="$t('printers.modal.sharePrinter.emailDoesNotBelong')" />
          </span>
          <span v-else-if="data.item.code === 2">
            {{$t('printers.modal.sharePrinter.notMemberOfOrg')}}
            <icon-info-circle v-b-tooltip.hover.leftbottom
                              :title="$t('printers.modal.sharePrinter.notMemberOfOrgDesc')" />
          </span>
        </template>
        <template v-slot:cell(code)="data">
          <div style="text-align: right">
          <span v-if="data.item.code < 0">
            <b-spinner small></b-spinner>
          </span>
            <span v-else-if="data.item.code === 0">
            <icon-check-circle />
          </span>
            <span v-else-if="data.item.code === 1">
            <b-button variant="-secondary btn--outline" size="-small" @click="confirm(data.item.email)">
              {{$t('printers.modal.sharePrinter.invite')}}
            </b-button>
          </span>
            <span v-else-if="data.item.code === 2">
            <b-button variant="-secondary btn--outline" size="-small" @click="confirm(data.item.email)">
              {{$t('printers.modal.sharePrinter.accept')}}
            </b-button>
          </span>
            <span v-else-if="data.item.code === 3">
            <icon-cancel-circle v-b-tooltip.hover.leftbottom :title="data.item.message" />
          </span>
          </div>
        </template>
      </b-table>
      </div>
      <template v-slot:modal-footer="{hide}">
        <b-container>
          <b-row>
            <b-col cols="auto" class="mr-auto">
              <b-button variant="-secondary btn--outline"
                        v-if="showInviteAllButton"
                        @click="confirmAllInvitation">
                {{$t('printers.modal.sharePrinter.inviteAll')}} <span class="text--muted">|</span> {{inviteButtonCount}}
              </b-button>
              <b-button variant="-secondary btn--outline"
                        v-if="showAcceptNonOrgMembersAllButton"
                        @click="confirmAllNotOrgMembers">
                {{$t('printers.modal.sharePrinter.acceptAllNonOrg')}} <span class="text--muted">|</span> {{acceptNonOrgMemberButtonCount}}
              </b-button>
            </b-col>
            <b-col cols="auto">
              <b-button variant="-ternary btn--outline"
                        :class="{'btn--disabled': !canCloseProcessingDialog}"
                        :disabled="!canCloseProcessingDialog"
                        @click="closeProgressModal">
                {{$t('buttons.ok')}}
              </b-button>
            </b-col>
          </b-row>
        </b-container>
      </template>
    </b-modal>
  </div>
</template>

<script>
  import Vue from "vue";
  import {mapActions, mapGetters} from "vuex";

  import IconTrash from "@/components/icons/IconTrash";
  import IconCrown from "@/components/icons/IconCrown";
  import IconCheckCircle from "@/components/icons/IconCheckCircle";
  import IconCancelCircle from "@/components/icons/IconCancelCircle";
  import IconInfoCircle from "@/components/icons/IconInfoCircle";

  function emailsStringToArray(emails) {
    emails = emails.replace(/[\n\r]/g, ',').split(',');
    let arr = [];
    for (let i in emails) {
      let email = emails[i];
      if ((email = email.trim()).length>0 && arr.indexOf(email)==-1) {
        arr.push(email);
      }
    }

    return arr;
  }

  function isValidEmailAddress(emailAddress) {
    const pattern = /^(([^<>()\\[\]\\.,;:\s@"]+(\.[^<>()\\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,24}))$/;
    return pattern.test(emailAddress);
  }

  export default {
    name: 'share-printer',
    props: ['printerId'],
    components: {
      IconTrash,
      IconCrown,
      IconCheckCircle,
      IconCancelCircle,
      IconInfoCircle
    },
    data() {
      return {
        loaded: false,
        users: [],
        emailValue: '',
        roleValue: 2,
        limitValue: null,
        fields: [
          {
            key: 'user',
            class: 'user'
          },
          {
            key: 'email',
          },
          {
            key: 'limit',
          },
          {
            key: 'del',
          },
        ],

        showProgress: false,
        emails: [],
        progressFields: [
          {key: 'email'},
          {key: 'type'},
          {key: 'code'}
        ]
      }
    },
    created() {
      this.loadSharingData();
    },
    computed: {
      ...mapGetters(['isUserInOrganisation']),

      roles() {
        if (this.isUserInOrganisation) {
          return {
            2: this.$t('printers.shareRoles.canPrint'),
            3: this.$t('printers.shareRoles.canPrintCustom'),
            4: this.$t('printers.shareRoles.canQueueCustom'),
            5: this.$t('printers.shareRoles.canQueue'),
          }
        }

        return {
          2: this.$t('printers.shareRoles.canPrint'),
          5: this.$t('printers.shareRoles.canQueue'),
        }
      },
      isValidEmail() {
        console.log('VALIDATE EMAIL');
        const emails = emailsStringToArray(this.emailValue);
        if (!emails.length) {
          return true;
        }
        for (let i in emails) {
          if (!isValidEmailAddress(emails[i])) {
            console.log('INVALID');
            return false;
          }
        }

        console.log('VALID');
        return true;
      },
      canCloseProcessingDialog() {
        if (!this.emails.length) {
          return true;
        }
        for (let i in this.emails) {
          if (this.emails[i].code === -1) {
            return false;
          }
        }

        return true;
      },
      showInviteAllButton() {
        if (this.emails.length <= 1) {
          return false;
        }
        for (let i in this.emails) {
          if (this.emails[i].code === 1) {
            return true;
          }
        }

        return false;
      },
      inviteButtonCount() {
        if (!this.emails.length) {
          return 0;
        }
        let count = 0;
        this.emails.map(data => {
          count += data.code === 1 ? 1 : 0;
        });

        return count;
      },
      showAcceptNonOrgMembersAllButton() {
        if (this.emails.length <= 1) {
          return false;
        }
        for (let i in this.emails) {
          if (this.emails[i].code === 2) {
            return true;
          }
        }

        return false;
      },
      acceptNonOrgMemberButtonCount() {
        if (!this.emails.length) {
          return 0;
        }
        let count = 0;
        this.emails.map(data => {
          count += data.code === 2 ? 1 : 0;
        });

        return count;
      }
    },
    methods: {
      ...mapActions(['getPrinterSharing', 'sharePrinter', 'unsharePrinter']),

      loadSharingData() {
        this.loaded = false;
        this.getPrinterSharing(this.printerId).then(({data}) => {
          this.loaded = true;
          if (data.result) {
            this.users = data.message.users_list
          } else {
            //TODO: close popup, show message
          }
        }).catch((e) => {
          console.log(e);
          //TODO: close popup, show message
        });
      },
      limitText(user) {
        if (user.printer_ownership_id == 1) {
          return this.$t('printers.modal.sharePrinter.owner');
        }
        let role = this.roles[user.printer_ownership_id];
        if (!user.number_of_prints) {
          role += ' ' + this.$t('printers.modal.sharePrinter.limitless');
        } else {
          role += ' ' + this.$t('printers.modal.sharePrinter.limitNumber', {number: user.number_of_prints});
        }

        return role;
      },
      unshare(id) {
        this.unsharePrinter(id)
          .then(({data}) => {
            if (data.result) {
              const userIndex = this.users.findIndex(share => share.id == id);
              if (userIndex >= 0) {
                Vue.delete(this.users, userIndex);
              }
            } else {
              this.msgBoxOk(data.message);
            }
          })
          .catch(() => {
            this.msgBoxOk(this.$t('printers.modal.sharePrinter.errorUnsharePrinter'));
          })
        ;
      },
      share() {
        const emails = emailsStringToArray(this.emailValue);
        if (!emails.length) {
          return;
        }
        this.showProgress = true;
        let emailsList = [];
        for (let i in emails) {
          emailsList.push({email: emails[i], code: -1, message: null}); //code 0 - in progress
        }
        this.emails = emailsList;
        this._shareNext(0, true);
      },
      confirm(email) {
        const emailIndex = this.emails.findIndex(data => data.email === email);
        if (emailIndex < 0) {
          return;
        }
        this.emails[emailIndex].code = -1;
        this._sendShare(email, false).then(({data}) => {
          if (data.result) {
            this.emails[emailIndex].code = 0;
          } else {
            this.emails[emailIndex].code = 3;
            this.emails[emailIndex].message = data.message;
          }
        })
      },
      confirmAllInvitation() {
        this.emails.map(data => {
          if (data.code === 1) {
            this.confirm(data.email);
          }
        });
      },
      confirmAllNotOrgMembers() {
        this.emails.map(data => {
          if (data.code === 2) {
            this.confirm(data.email);
          }
        });
      },
      closeProgressModal() {
        this.showProgress = false;
        this.emailValue = '';
        this.roleValue = 2;
        this.limitValue = null;
        this.loadSharingData();
      },
      _shareNext(index, checkUser) {
        if (this.emails.length <= index) {
          return;
        }
        const data = this.emails[index];
        this._sendShare(data.email, checkUser).then(({data}) => {
          if (data.result) {
            this.emails[index].code = 0;
          } else {
            if (data.message.trim() === this.$t('printers.modal.sharePrinter.errorEmailDontBelong')) {
              this.emails[index].code = 1;
            } else if (data.message.trim() === this.$t('printers.modal.sharePrinter.errorNotMember')) {
              this.emails[index].code = 2;
            } else {
              this.emails[index].code = 3;
              if (data.message.indexOf('Email is not valid') === 0) {
                this.emails[index].message = this.$t('printers.modal.sharePrinter.invalidEmail');
              } else {
                this.emails[index].message = data.message;
              }
            }
          }
        });
        this._shareNext(index+1, checkUser);
      },
      _sendShare(email, checkUser) {
        const res = this.sharePrinter({
          printerId: this.printerId,
          email: email,
          role: this.roleValue,
          limit: this.limitValue,
          checkUser: checkUser
        });
        res.catch(() => {

        });

        return res;
      }
    }
  }
</script>

<style scoped>
  .table.table--sharing {
    position:relative;
    max-height:300px;
    overflow-y:scroll;
  }
  .td-centered {
    text-align: center;
  }
</style>
